import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import { Loader2, Wand, Plus, ChevronDown, Check } from "lucide-react";
import { GeminiGenerateContent, RetrieveContexts } from "../../../api/APIGemini";
import { GetGeminiProject, GetGeminiLocation, GetSupportTopics } from "../../../api/APIBot";
import { GetCorpusCodes } from "../../../api/APISurvey";
import { getTopicDisplayName } from "../../../utils/topicUtils";
import { AutoResizeTextarea } from "../../../UIElements";
import { ReactComponent as CloseIcon } from "../../../svg/close.svg";
import "./ModuleGeneratorModal.scss";

const defaultConfig = {
    versionGroup: "",
    versions: [],
    version: "DEFAULT",
    coordinator: "",
    supportRequestTarget: "",
    supportTopics: [],
    materials: [],
    color: "#9EA5F8",
    icon: "Bot",
    topicData: {},
    topicSettings: {},
    corpora: {},
};

function deriveSlug(title) {
    return (title || "kb").toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "") || "kb";
}

export default function ModuleGeneratorModal({ geminiConfig, onClose }) {
    const intl = useIntl();
    const history = useHistory();
    const [phase, setPhase] = useState("select"); // "select" | "form" | "loading"

    const handleCreateFromScratch = () => {
        onClose();
        history.push("/support-bot/new");
    };

    return (
        <div className="modal-container module-generator-modal">
            <div className="close-modal" onClick={onClose} />
            <div className="modal">
                <div className="close-toggle-container">
                    <div className="close-toggle" onClick={onClose}>
                        <CloseIcon />
                    </div>
                </div>

                {phase === "select" && (
                    <MethodSelection
                        onGenerate={() => setPhase("form")}
                        onScratch={handleCreateFromScratch}
                        intl={intl}
                    />
                )}

                {phase === "form" && (
                    <GeneratorForm
                        geminiConfig={geminiConfig}
                        onBack={() => setPhase("select")}
                        onClose={onClose}
                        intl={intl}
                        history={history}
                        setPhase={setPhase}
                    />
                )}

                {phase === "loading" && null /* Loading is rendered inside GeneratorForm */}
            </div>
        </div>
    );
}

function MethodSelection({ onGenerate, onScratch, intl }) {
    return (
        <div className="method-selection">
            <h3>
                {intl.formatMessage({
                    id: "moduleGenerator.title",
                    defaultMessage: "Create new bot module",
                })}
            </h3>
            <div className="method-cards">
                <button className="method-card generate" onClick={onGenerate}>
                    <Wand size={24} />
                    <span className="method-title">
                        {intl.formatMessage({
                            id: "moduleGenerator.generateWithAI",
                            defaultMessage: "Generate with AI",
                        })}
                    </span>
                    <span className="method-description">
                        {intl.formatMessage({
                            id: "moduleGenerator.generateDescription",
                            defaultMessage: "Describe the module's purpose and audience, and AI will generate the instructions",
                        })}
                    </span>
                </button>
                <button className="method-card scratch" onClick={onScratch}>
                    <Plus size={24} />
                    <span className="method-title">
                        {intl.formatMessage({
                            id: "moduleGenerator.createFromScratch",
                            defaultMessage: "Create from scratch",
                        })}
                    </span>
                    <span className="method-description">
                        {intl.formatMessage({
                            id: "moduleGenerator.scratchDescription",
                            defaultMessage: "Start with an empty module and write the instructions yourself",
                        })}
                    </span>
                </button>
            </div>
        </div>
    );
}

function GeneratorForm({ geminiConfig, onBack, onClose, intl, history, setPhase }) {
    const moduleGenerator = geminiConfig.moduleGenerator;

    const [purpose, setPurpose] = useState("");
    const [audience, setAudience] = useState("");
    const [conversationFlow, setConversationFlow] = useState("");
    const [showConversationFlow, setShowConversationFlow] = useState(false);
    const [selectedCorpusId, setSelectedCorpusId] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [loadingStep, setLoadingStep] = useState(0);
    const [error, setError] = useState(null);

    // API hooks
    const geminiGenerateContent = GeminiGenerateContent();
    const retrieveContexts = RetrieveContexts();
    const geminiProjectQuery = GetGeminiProject();
    const geminiLocationQuery = GetGeminiLocation();
    const corpusCodesQuery = GetCorpusCodes();
    const supportTopicsQuery = GetSupportTopics();

    const supportTopicCodes = supportTopicsQuery.data?.[0] || {};
    const taxonomyTopics = Object.entries(supportTopicCodes)
        .filter(([, data]) => data.taxonomy)
        .map(([code, data]) => ({
            code,
            name: getTopicDisplayName(code, data, intl),
        }));

    const corpusCodesMap = corpusCodesQuery.data?.[0] || {};
    const corpusList = Object.entries(corpusCodesMap).map(([code, value]) => ({
        code,
        title: value?.title || code,
    }));

    const project = geminiProjectQuery?.data?.[0]?.value;
    const location = geminiLocationQuery?.data?.[0]?.value;

    const canGenerate = purpose.trim() && audience.trim() && !isGenerating;

    const handleGenerate = async () => {
        setIsGenerating(true);
        setError(null);
        setLoadingStep(1);

        try {
            let referenceTexts = [];

            if (selectedCorpusId && project && location) {
                // Step 1: Generate RAG queries
                const queryResponse = await geminiGenerateContent.mutateAsync({
                    model: moduleGenerator.queryGenerator.model,
                    requestBody: {
                        systemInstruction: { parts: [{ text: moduleGenerator.queryGenerator.prompt }] },
                        contents: [{ role: "user", parts: [{ text: `purpose: ${purpose}\naudience: ${audience}` }] }],
                        generationConfig: moduleGenerator.queryGenerator.generationConfig,
                    },
                });

                let queries = [];
                try {
                    const queryText = queryResponse.candidates?.[0]?.content?.parts?.find(p => p.text)?.text || "{}";
                    queries = JSON.parse(queryText).queries || [];
                } catch (e) {
                    console.warn("Failed to parse query generator response, skipping RAG:", e);
                }

                // Step 2: Retrieve contexts for each query (parallel)
                if (queries.length > 0) {
                    setLoadingStep(2);
                    const corpusPath = `projects/${project}/locations/${location}/ragCorpora/${selectedCorpusId}`;
                    const ragConfig = moduleGenerator.ragConfig || {};

                    const retrievalResults = await Promise.all(
                        queries.map(query =>
                            retrieveContexts.mutateAsync({
                                vertex_rag_store: { rag_resources: [{ rag_corpus: corpusPath }] },
                                query: {
                                    text: query,
                                    rag_retrieval_config: {
                                        top_k: ragConfig.topK || 20,
                                        ...(ragConfig.vectorDistanceThreshold !== null
                                            ? { filter: { vector_distance_threshold: ragConfig.vectorDistanceThreshold } }
                                            : {}),
                                    },
                                },
                            }).catch(err => {
                                console.warn("RAG query failed:", query, err);
                                return null;
                            })
                        )
                    );

                    // Deduplicate results
                    const seenTexts = new Set();
                    retrievalResults.forEach(result => {
                        (result?.contexts?.contexts || []).forEach(ctx => {
                            if (ctx.text && !seenTexts.has(ctx.text)) {
                                seenTexts.add(ctx.text);
                                referenceTexts.push(ctx.text);
                            }
                        });
                    });
                }
            }

            // Step 3: Generate module
            setLoadingStep(3);
            const topicListJson = taxonomyTopics.length > 0
                ? `available_topics: ${JSON.stringify(taxonomyTopics.map(t => ({ code: t.code, name: t.name })))}`
                : null;

            const userMessage = [
                `purpose: ${purpose}`,
                `audience: ${audience}`,
                conversationFlow.trim() ? `conversation_flow: ${conversationFlow}` : null,
                referenceTexts.length > 0 ? `reference_material: ${referenceTexts.join("\n\n")}` : null,
                topicListJson,
            ].filter(Boolean).join("\n\n");

            const moduleResponse = await geminiGenerateContent.mutateAsync({
                model: moduleGenerator.promptGenerator.model,
                requestBody: {
                    systemInstruction: { parts: [{ text: moduleGenerator.promptGenerator.prompt }] },
                    contents: [{ role: "user", parts: [{ text: userMessage }] }],
                    generationConfig: moduleGenerator.promptGenerator.generationConfig,
                },
            });

            const finishReason = moduleResponse.candidates?.[0]?.finishReason;
            if (finishReason === "MAX_TOKENS") {
                throw new Error("Response truncated — output token limit reached");
            }

            const resultText = moduleResponse.candidates?.[0]?.content?.parts?.find(p => p.text)?.text;
            const result = JSON.parse(resultText);

            // Build corpora map if a corpus was selected
            const corpora = {};
            if (selectedCorpusId) {
                const selectedCorpus = corpusList.find(kb => kb.code === selectedCorpusId);
                const slug = deriveSlug(selectedCorpus?.title);
                corpora[slug] = selectedCorpusId;
            }

            // Validate AI-selected topics against actual taxonomy codes
            const validTopicCodes = new Set(taxonomyTopics.map(t => t.code));
            const selectedTopics = (result.supportTopics || []).filter(code => validTopicCodes.has(code));

            // Build topicData for selected topics
            const topicData = {};
            selectedTopics.forEach(code => {
                if (supportTopicCodes[code]) {
                    topicData[code] = { ...supportTopicCodes[code] };
                }
            });

            // Build topicSettings from AI-generated topic instructions
            const topicSettings = {};
            (result.topicInstructions || []).forEach(({ code, instructions }) => {
                if (selectedTopics.includes(code) && instructions) {
                    topicSettings[code] = {
                        instructions,
                        requesttype: supportTopicCodes[code]?.requesttype || "requestWithConsent",
                        followuptype: supportTopicCodes[code]?.followuptype || "",
                    };
                }
            });

            // Store template and navigate
            sessionStorage.setItem("newBotTemplate", JSON.stringify({
                config: {
                    ...defaultConfig,
                    name: result.name || "",
                    description: result.description || "",
                    generalInstructions: result.generalInstructions || "",
                    supportTopics: selectedTopics,
                    topicData,
                    topicSettings,
                    corpora,
                },
            }));

            onClose();
            history.push("/support-bot/new");
        } catch (err) {
            console.error("Module generation failed:", err);
            setError(intl.formatMessage({
                id: "moduleGenerator.error",
                defaultMessage: "Generation failed. Please try again.",
            }));
            setIsGenerating(false);
            setLoadingStep(0);
        }
    };

    if (isGenerating) {
        return <LoadingState loadingStep={loadingStep} hasCorpus={!!selectedCorpusId} intl={intl} />;
    }

    return (
        <div className="generator-form">
            <h3>
                {intl.formatMessage({
                    id: "moduleGenerator.formTitle",
                    defaultMessage: "Generate module",
                })}
            </h3>

            {error && <div className="generator-error">{error}</div>}

            <div className="form-field">
                <h4>
                    {intl.formatMessage({
                        id: "moduleGenerator.purpose",
                        defaultMessage: "What should this module help with?",
                    })}
                </h4>
                <AutoResizeTextarea
                    id="gen-purpose"
                    value={purpose}
                    onChange={e => setPurpose(e.target.value)}
                    placeholder={intl.formatMessage({ id: "moduleGenerator.purpose.placeholder", defaultMessage: "e.g. Helping prospective applicants explore study programs and guide them toward applying" })}
                    rows={2}
                    maxRows={6}
                />
            </div>

            <div className="form-field">
                <h4>
                    {intl.formatMessage({
                        id: "moduleGenerator.audience",
                        defaultMessage: "Who will be talking to the bot?",
                    })}
                </h4>
                <AutoResizeTextarea
                    id="gen-audience"
                    value={audience}
                    onChange={e => setAudience(e.target.value)}
                    placeholder={intl.formatMessage({ id: "moduleGenerator.audience.placeholder", defaultMessage: "e.g. Prospective applicants, typically working professionals or entrepreneurs considering further education" })}
                    rows={2}
                    maxRows={6}
                />
            </div>

            <div className="form-field optional">
                <button
                    className="optional-toggle"
                    onClick={() => setShowConversationFlow(!showConversationFlow)}
                    type="button"
                >
                    <ChevronDown
                        size={16}
                        style={{ transform: showConversationFlow ? "rotate(180deg)" : "rotate(0deg)" }}
                    />
                    <h4>
                        {intl.formatMessage({
                            id: "moduleGenerator.conversationFlow",
                            defaultMessage: "How should a typical conversation go?",
                        })}
                    </h4>
                    <span className="optional-label">
                        {intl.formatMessage({
                            id: "moduleGenerator.optional",
                            defaultMessage: "optional",
                        })}
                    </span>
                </button>
                {showConversationFlow && (
                    <AutoResizeTextarea
                        id="gen-flow"
                        value={conversationFlow}
                        onChange={e => setConversationFlow(e.target.value)}
                        placeholder={intl.formatMessage({ id: "moduleGenerator.conversationFlow.placeholder", defaultMessage: "e.g. Start by understanding the person's situation and goals. Help them find the right program. Explain how studies work in practice. Close with clear next steps on how to apply." })}
                        rows={3}
                        maxRows={8}
                    />
                )}
            </div>

            {corpusList.length > 0 && (
                <div className="form-field">
                    <h4>
                        {intl.formatMessage({
                            id: "moduleGenerator.knowledgeBase",
                            defaultMessage: "Knowledge base",
                        })}
                        <span className="optional-label">
                            {intl.formatMessage({
                                id: "moduleGenerator.optional",
                                defaultMessage: "optional",
                            })}
                        </span>
                    </h4>
                    <select
                        id="gen-corpus"
                        value={selectedCorpusId}
                        onChange={e => setSelectedCorpusId(e.target.value)}
                    >
                        <option value="">
                            {intl.formatMessage({
                                id: "moduleGenerator.noKnowledgeBase",
                                defaultMessage: "None — generate without reference material",
                            })}
                        </option>
                        {corpusList.map(kb => (
                            <option key={kb.code} value={kb.code}>
                                {kb.title}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className="modal-options">
                <button className="cancel" onClick={onBack}>
                    {intl.formatMessage({
                        id: "moduleGenerator.back",
                        defaultMessage: "Back",
                    })}
                </button>
                <button
                    className="confirm"
                    onClick={handleGenerate}
                    disabled={!canGenerate}
                >
                    <Wand size={16} />
                    {intl.formatMessage({
                        id: "moduleGenerator.generate",
                        defaultMessage: "Generate",
                    })}
                </button>
            </div>
        </div>
    );
}

function LoadingState({ loadingStep, hasCorpus, intl }) {
    const [, setTick] = useState(0);
    const [stepStartTime, setStepStartTime] = useState(Date.now());
    const [finishedElapsed, setFinishedElapsed] = useState({});
    const prevLoadingStep = useRef(loadingStep);

    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (loadingStep !== prevLoadingStep.current) {
            const elapsed = Math.floor((Date.now() - stepStartTime) / 1000);
            setFinishedElapsed(prev => ({ ...prev, [prevLoadingStep.current]: elapsed }));
            setStepStartTime(Date.now());
            prevLoadingStep.current = loadingStep;
        }
    }, [loadingStep, stepStartTime]);

    const currentElapsed = Math.floor((Date.now() - stepStartTime) / 1000);

    const steps = [
        {
            key: 1,
            label: intl.formatMessage({
                id: "moduleGenerator.step.queries",
                defaultMessage: "Generating search queries",
            }),
            show: hasCorpus,
        },
        {
            key: 2,
            label: intl.formatMessage({
                id: "moduleGenerator.step.searching",
                defaultMessage: "Searching knowledge base",
            }),
            show: hasCorpus,
        },
        {
            key: 3,
            label: intl.formatMessage({
                id: "moduleGenerator.step.generating",
                defaultMessage: "Generating module",
            }),
            show: true,
        },
    ].filter(s => s.show);

    return (
        <div className="loading-state">
            <h3>
                {intl.formatMessage({
                    id: "moduleGenerator.generatingTitle",
                    defaultMessage: "Generating module",
                })}
            </h3>
            <div className="loading-steps">
                {steps.map(step => {
                    const status = loadingStep > step.key ? "done" : loadingStep === step.key ? "active" : "pending";
                    const elapsed = status === "done" ? finishedElapsed[step.key] : status === "active" ? currentElapsed : null;
                    return (
                        <div key={step.key} className={`loading-step ${status}`}>
                            {status === "done" ? (
                                <Check size={16} />
                            ) : status === "active" ? (
                                <Loader2 size={16} className="spinner" />
                            ) : (
                                <span className="step-dot" />
                            )}
                            <span className="step-label">{step.label}</span>
                            {elapsed != null && (
                                <span className="step-elapsed">
                                    {status === "active" ? `(${elapsed}s)` : `${elapsed}s`}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
